<script setup>
import { ref } from 'vue'

const newsletterEmail = ref('')
const newsletterHoneypot = ref('')
const newsletterTimestamp = ref(Date.now())
const newsletterLoading = ref(false)
const newsletterSuccess = ref(false)
const newsletterError = ref('')

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const submitNewsletter = async () => {
  newsletterError.value = ''
  if (!newsletterEmail.value.trim()) {
    newsletterError.value = 'กรุณากรอกอีเมลของคุณ'
    return
  }
  if (!isValidEmail(newsletterEmail.value.trim())) {
    newsletterError.value = 'รูปแบบอีเมลไม่ถูกต้อง'
    return
  }
  newsletterLoading.value = true
  try {
    const res = await fetch('/api/newsletter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: newsletterEmail.value.trim(),
        honeypot: newsletterHoneypot.value,
        timestamp: newsletterTimestamp.value
      })
    })
    const data = await res.json()
    if (data.success) {
      newsletterSuccess.value = true
      newsletterEmail.value = ''
    } else {
      newsletterError.value = data.error || 'เกิดข้อผิดพลาด'
    }
  } catch (e) {
    newsletterError.value = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้'
  } finally {
    newsletterLoading.value = false
  }
}
</script>

<template>
  <div class="w-full">
    <form @submit.prevent="submitNewsletter" class="relative">
      <input type="text" name="b_name" tabindex="-1" v-model="newsletterHoneypot" class="hidden">
      <template v-if="newsletterSuccess">
        <div class="w-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 rounded-2xl py-4 px-5 flex items-center justify-center gap-3 animate-fade-in shadow-sm">
          <div class="w-9 h-9 rounded-xl bg-[#0b533b] text-white flex items-center justify-center shrink-0 shadow-sm shadow-emerald-950/20">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div class="text-left">
            <p class="text-sm font-black text-emerald-950 dark:text-emerald-300">ลงทะเบียนรับสิทธิพิเศษสำเร็จ!</p>
            <p class="text-xs text-emerald-800 dark:text-emerald-300">ขอบคุณที่ร่วมเป็นครอบครัว Outdoor Lander Privilege Club</p>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="relative flex flex-col sm:flex-row items-stretch sm:items-center bg-slate-50 hover:bg-white dark:bg-white/[0.06] dark:hover:bg-white/[0.09] border border-slate-200/90 dark:border-white/15 focus-within:bg-white focus-within:border-emerald-500 dark:focus-within:border-emerald-400/80 rounded-2xl p-1.5 focus-within:ring-4 focus-within:ring-emerald-500/15 transition-all duration-300 shadow-inner">
          <div class="flex items-center flex-1 min-w-0 pl-3">
            <div class="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <input 
              type="email" 
              v-model="newsletterEmail"
              placeholder="กรอกอีเมลของคุณเพื่อรับสิทธิพิเศษ..." 
              class="w-full pl-3 pr-3 py-3 text-sm sm:text-[15px] font-medium bg-transparent border-none focus:ring-0 outline-none text-slate-900 dark:text-white placeholder:text-slate-500 dark:placeholder:text-gray-400"
            >
          </div>
          <button 
            type="submit" 
            :disabled="newsletterLoading"
            class="mt-1.5 sm:mt-0 px-6 py-3.5 bg-gradient-to-r from-emerald-800 to-teal-800 hover:from-emerald-700 hover:to-teal-700 text-white text-sm font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap shadow-md shadow-emerald-950/20 hover:shadow-lg hover:shadow-emerald-950/30 disabled:opacity-70 cursor-pointer active:scale-95"
          >
            <svg v-if="newsletterLoading" class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            <template v-else>
              <span>รับสิทธิพิเศษ</span>
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
              </svg>
            </template>
          </button>
        </div>
        <p v-if="newsletterError" class="mt-2 text-xs text-red-600 dark:text-rose-400 font-semibold flex items-center gap-1">
          <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {{ newsletterError }}
        </p>
      </template>
    </form>
  </div>
</template>
